"use strict";
// Clinical score-map: α-sign-aware response flipping at the questionnaire layer.
//
// SHAW-CAT and other "negative-α as reverse-keying" calibrations encode item
// direction in the sign of α. To score these uniformly with a 2PL GRM where the
// engine consumes α as-is, the patient's clinical response must be flipped on
// reverse-keyed items so the GRM ordinal aligns with the latent-trait direction
// the calibration was fit against.
//
// Rule:
//   For an item with α ≥ 0 → pass response value through unchanged
//   For an item with α < 0 → replace value with (K - value)
// where K is the per-item top ordinal (auto-detected as max numeric `value`,
// or supplied via `kOverride`).
//
// This is the architecture Promin / Concerto use internally via per-item
// scoreMap authoring. Putting it in the questionnaire layer (rather than the
// engine) keeps the engine a pure GRM and avoids special-casing α<0 inside
// `getParamsFromConfig`.
Object.defineProperty(exports, "__esModule", { value: true });
exports.hasNegativeAlphaItems = exports.applyClinicalScoreMap = void 0;
/**
 * @typedef {import('./types').IRTConfig} IRTConfig
 * @typedef {import('./types').Response} Response
 */
/**
 * Best-effort detection of the top ordinal K for an item: the largest finite
 * numeric `value` across its coefficients. Returns `undefined` when the item
 * has no numeric values (the calling code should then leave the response
 * untouched).
 *
 * @param {{coefficients: Array<{value: string|number}>}} item
 * @returns {number | undefined}
 */
function autoDetectK(item) {
    if (!item || !Array.isArray(item.coefficients))
        return undefined;
    let max = -Infinity;
    item.coefficients.forEach((c) => {
        const n = Number(c && c.value);
        if (Number.isFinite(n) && n > max)
            max = n;
    });
    return Number.isFinite(max) ? max : undefined;
}
/**
 * Apply the clinical score-map flip to a response set. Items with α<0 in the
 * supplied calibration get their response value replaced with (K - value).
 * Items with α≥0 (or items missing from the config) pass through unchanged.
 *
 * RSM bypass: under Andrich RSM (Singapore SHAW-CAT and any other rating-scale
 * calibration where item discrimination is fixed at 1.0), the per-item "alpha"
 * field is actually the item-location δ_i, not a discrimination parameter.
 * Negative δ values do not encode direction; they just indicate easy items on
 * the trait continuum. For these calibrations the clinical score-map flip is
 * mathematically incorrect and is skipped — direction is encoded via the
 * coefficient-level `score` field instead, which the engine reads directly.
 * The bypass is opt-in via the `calibrationClass: 'RSM'` option.
 *
 * @param {Response[]} responses
 * @param {IRTConfig | undefined | null} config
 * @param {number | { kOverride?: number, calibrationClass?: 'RSM' | 'GRM' }} [kOverrideOrOptions]
 * @returns {Response[]}
 */
function applyClinicalScoreMap(responses, config, kOverrideOrOptions) {
    if (!Array.isArray(responses) || !config)
        return responses;
    // Options-object form
    let kOverride;
    let calibrationClass;
    if (typeof kOverrideOrOptions === 'object' && kOverrideOrOptions !== null) {
        kOverride = kOverrideOrOptions.kOverride;
        calibrationClass = kOverrideOrOptions.calibrationClass;
    }
    else {
        kOverride = kOverrideOrOptions;
    }
    // RSM bypass — no α-sign flipping under rating-scale interpretation.
    if (calibrationClass === 'RSM')
        return responses;
    return responses.map((r) => {
        const item = config[r.questionId];
        if (!item || typeof item.alpha !== 'number')
            return r;
        if (item.alpha >= 0)
            return r;
        const K = kOverride !== null && kOverride !== void 0 ? kOverride : autoDetectK(item);
        if (K == null || !Number.isFinite(K))
            return r;
        const clinical = Number(r.answer && r.answer.value);
        if (!Number.isFinite(clinical))
            return r;
        return Object.assign(Object.assign({}, r), { answer: Object.assign(Object.assign({}, r.answer), { value: String(K - clinical) }) });
    });
}
exports.applyClinicalScoreMap = applyClinicalScoreMap;
/**
 * Convenience predicate: does the calibration contain any α<0 items?
 * Useful for authoring guards (warn/refuse to enable CLINICAL score-map on a
 * bank where it would be a no-op).
 *
 * @param {IRTConfig | undefined | null} config
 * @returns {boolean}
 */
function hasNegativeAlphaItems(config) {
    if (!config)
        return false;
    return Object.keys(config).some((key) => {
        const item = config[key];
        return item && typeof item.alpha === 'number' && item.alpha < 0;
    });
}
exports.hasNegativeAlphaItems = hasNegativeAlphaItems;
//# sourceMappingURL=scoreMap.js.map